import { z } from 'zod';

import { FlightOffersBlockSchema } from './flight/flight_offers/flight_offers_schema.js';
import { FlightMultiOffersBlockSchema } from './flight/flight_multi_offers/flight_multi_offers_schema.js';
import { FlightCheaperDatesBlockSchema } from './flight/flight_cheaper_dates/flight_cheaper_dates_schema.js';
import { FlightPlannerBlockSchema } from './flight/flight_planner/flight_planner_schema.js';
import { FlightSearchConfirmationBlockSchema } from './flight/flight_search_confirmation/flight_search_confirmation_schema.js';
import { FlightReservationBlockSchema } from './flight/flight_reservation/flight_reservation_schema.js';
import {
  BusNextServicesBlockSchema,
  FlightNextServicesBlockSchema,
  HotelNextServicesBlockSchema,
  MobilityNextServicesBlockSchema,
  RentalNextServicesBlockSchema,
} from './business/next_services/next_services_schema.js';
import { ProductCheckoutBlockSchema } from './business/product_checkout/product_checkout_schema.js';
import { CheckoutOutcomeBlockSchema } from './business/checkout_outcome/checkout_outcome_schema.js';
import { VoucherCancellationBlockSchema } from './voucher/voucher_cancellation/voucher_cancellation_schema.js';
import { VoucherCancellationBulkBlockSchema } from './voucher/voucher_cancellation_bulk/voucher_cancellation_bulk_schema.js';
import { RentalOffersBlockSchema } from './rental/rental_offers/rental_offers_schema.js';
import { RentalPlannerBlockSchema } from './rental/rental_planner/rental_planner_schema.js';
import { RentalPriceChangeBlockSchema } from './rental/rental_price_change/rental_price_change_schema.js';
import { HotelPlannerBlockSchema } from './hotel/hotel_planner/hotel_planner_schema.js';
import { HotelRoomsBlockSchema } from './hotel/hotel_rooms/hotel_rooms_schema.js';
import { ConsultPlacesBlockSchema } from './places/consult_places/consult_places_schema.js';
import { HotelRateWarningBlockSchema } from './hotel/hotel_rate_warning/hotel_rate_warning_schema.js';
import { MobilityOffersBlockSchema } from './mobility/mobility_offers/mobility_offers_schema.js';
import { MobilityPlannerBlockSchema } from './mobility/mobility_planner/mobility_planner_schema.js';
import { MobilityRideDetailsBlockSchema } from './mobility/mobility_ride_details/mobility_ride_details_schema.js';
import { Ride99LinkBlockSchema } from './mobility/ride99_link/ride99_link_schema.js';
import { BusOffersBlockSchema } from './bus/bus_offers/bus_offers_schema.js';
import { BusStopsBlockSchema } from './bus/bus_stops/bus_stops_schema.js';
import { BusPlannerBlockSchema } from './bus/bus_planner/bus_planner_schema.js';
import { BusSeatMapBlockSchema } from './bus/bus_seat_map/bus_seat_map_schema.js';
import { TripsBlockSchema } from './trips/trips/trips_schema.js';
import { FlightSeatLinkBlockSchema } from './trips/flight_seat_link/flight_seat_link_schema.js';
import {
  ApprovalRequestBlockSchema,
  ApprovalResultBlockSchema,
  ApprovalBulkResultBlockSchema,
} from './approval/approval/approval_schema.js';
import { ApprovalStackBlockSchema } from './approval/approval_stack/approval_stack_schema.js';
import { GuidedFlowsBlockSchema } from './common/guided_flows/guided_flows_schema.js';
import { WalletLinkBlockSchema } from './common/wallet_link/wallet_link_schema.js';
import { ConfirmPromptBlockSchema } from './common/confirm_prompt/confirm_prompt_schema.js';
import { QuestionPromptBlockSchema } from './common/question_prompt/question_prompt_schema.js';
import { EventPremisesBlockSchema } from './events/event_premises/event_premises_schema.js';

/**
 * `@voll-intelligence/genui-kit` (entrada `.`, zero React) — contrato
 * compartilhado da UI generativa do canal app. Fonte ÚNICA dos schemas: cada
 * `kind` vive em `<catalog>/<component>/<component>_schema.ts` (co-localizado
 * com a sua UI), e este arquivo apenas MONTA o contrato a partir deles —
 * reexportando a API de schema e derivando `ChatUiSchema`/`CATALOG`.
 *
 * Modelo A2UI: o agente emite uma LISTA de components (`ChatUiMessage`); cada
 * component é um `kind` com 1 schema, 1 slot e 1 render. Composição = a lista
 * (não há `composite`). `id` por bloco garante dedup na borda de render.
 *
 *  - `ChatUiSchema` + `CHAT_UI_KINDS`: o union discriminado de UM component e a
 *    âncora runtime dos kinds, montados dos `*_schema.ts`.
 *  - `ChatUiMessageSchema`: a LISTA emitida por turno (ids únicos).
 *  - `CATALOG`: descoberta por `kind` (interativo? interactionPath? contextos?).
 *    O `slot` (chat/header) vive no `REGISTRY` da entrada `./react` (render).
 *  - `CHAT_UI_CONTEXTS` / `CATALOGS` / `kindsFor` / `isKindInContext`: o catálogo
 *    fatiado por CONTEXTO de uso (`common` | `flight` | `rental` | `trips`).
 *  - `interactionIdOf`: extrai o interactionId/challengeId derivando do
 *    `interactionPath` do `CATALOG`.
 *
 * A entrada `./react` (dispatcher + renderers) vive em `./react.tsx` e é a única
 * que importa React — o backend consome só esta entrada.
 */

// Reexport da API pública de schema (cada componente é dono do seu *_schema.ts).
export * from './shared/schema/money_schema.js';
export * from './shared/schema/document_schema.js';
export * from './shared/schema/flight_segment_schema.js';
export * from './shared/schema/flight_passengers_schema.js';
export { TravelerRefSchema, type TravelerRef } from './shared/schema/traveler_ref.js';
export * from './shared/schema/limits.js';
export * from './shared/schema/trip_voucher_schema.js';
export * from './shared/ui_resolution_schema.js';
export * from './shared/retired_kinds.js';
// Prosa de fornecedor vira TEXTO PURO (#2758): o modal-session limpa na raiz,
// as views limpam no replay — uma regra só, não duas implementações.
export { htmlToPlainText } from './shared/html_to_plain_text.js';
export {
  destinationImageByCity,
  destinationImageByIata,
} from './flight/shared/destination_image.js';
// A lente de localidade (mapa do destino) é compartilhada por /requested e
// /approvals: o modelo puro mora aqui porque só o genui-kit vê domain + kit.
export {
  hotelPinTrip,
  placeLensModel,
  tripAirports,
  type PlaceLensModel,
  type PlaceLensPin,
  type PlaceLensRoute,
  type PlaceLensTrip,
  type PlaceSelection,
} from './shared/place_lens/place_lens.js';
export * from './flight/flight_offers/flight_offers_schema.js';
// Cortes e facetas do filtro server-side — o shell implementa o `refine` do seam.
export type {
  FlightServerCuts,
  FlightServerFacets,
  FlightServerOrder,
} from './flight/shared/filter_wire.js';
export * from './flight/flight_multi_offers/flight_multi_offers_schema.js';
export {
  FlightTariffViewSchema,
  FlightTariffViewWireSchema,
  FlightOfferStaleError,
} from './flight/flight_offers/tariff_view.js';
export type {
  FlightTariffView,
  FlightFareView,
  FlightTariffPort,
} from './flight/flight_offers/tariff_view.js';
export type { FlightPlannerPort } from './flight/flight_planner/airport_view.js';
export type { FlightAirportHistoryPort } from './flight/shared/airport_history_view.js';
export * from './flight/flight_cheaper_dates/flight_cheaper_dates_schema.js';
export * from './flight/flight_planner/flight_planner_schema.js';
// Puro (só tipos importados): a ORDEM da doca é regra do card, e o agente projeta
// a cena do hotel com a MESMA função para numerar como o cliente vê.
export { deckOffersFor } from './hotel/hotel_planner/deck_offers.js';
// Puros: o glifo e a segunda linha de um lugar do Google são regra do campo `Onde`,
// e o menu de menções do web-chat desenha o MESMO lugar com a mesma receita.
export {
  isCityLevelPlace,
  placeIconFor,
  placeLocality,
  placeSecondLine,
} from './hotel/hotel_planner/place_type.js';
// Puro: a volta parte de outro aeroporto que o da chegada da ida (#4203) — a lista e a
// tela de tarifas da bancada e o roteiro do trip-panel leem a MESMA régua.
export {
  airportLabel,
  airportSwitchOf,
  type AirportSwitch,
} from './flight/shared/airport_switch.js';
export * from './flight/flight_search_confirmation/flight_search_confirmation_schema.js';
export * from './flight/flight_reservation/flight_reservation_schema.js';
export * from './business/product_checkout/corporate_booking_form.js';
export * from './business/product_checkout/allocation_scope.js';
export * from './business/product_checkout/product_checkout_schema.js';
export * from './business/product_checkout/normalize_travelers.js';
export * from './business/product_checkout/allocation_owner.js';
export * from './business/product_checkout/room_holder_allocation.js';
export * from './business/product_checkout/prefill.js';
export * from './business/product_checkout/core/can_submit.js';
export {
  BUS_CONCIERGE_FIELDS,
  CONCIERGE_FIELDS,
  conciergeFieldsFor,
  fieldsFor,
  offerFieldsFor,
} from './business/product_checkout/core/field_manifest.js';
export type { CheckoutProductType } from './business/product_checkout/product_type.js';
export * from './business/concierge_checkout/concierge_checkout_schema.js';
// Reserva de VOO (puro, zero IO): montagem do voucher corporativo + os mappers
// para o fio do modal-session. Compartilhado entre o caminho conversacional
// (agents) e o determinístico da bancada (app-integration) — uma regra só de
// documento, centro de custo e nome de embarque, centralizada ANTES de o
// segundo produtor existir.
export {
  buildCorporateVoucherFromCheckout,
  type CorporateVoucher,
  type FlightCheckoutProfile,
} from './flight/shared/booking/voucher.js';
export {
  toModalCorporateSelection,
  toModalTravelerInputs,
  voucherTravelerGap,
} from './flight/shared/booking/modal_booking_payload.js';
export { flightQuoteWithinPolicy } from './flight/shared/booking/policy.js';
export { sanitizeModalIdempotencyKey } from './flight/shared/booking/idempotency.js';
export {
  isHalfSplitPair,
  matchPricingMatrixCell,
  splitModalFarePair,
} from './flight/shared/booking/fare_pair.js';
export * from './approval/approval/approval_schema.js';
export * from './approval/approval_stack/approval_stack_schema.js';
export * from './voucher/voucher_cancellation/voucher_cancellation_schema.js';
export * from './voucher/voucher_cancellation_bulk/voucher_cancellation_bulk_schema.js';
export * from './rental/rental_offers/rental_offers_schema.js';
export * from './rental/rental_planner/rental_planner_schema.js';
export * from './rental/rental_price_change/rental_price_change_schema.js';
export * from './hotel/shared/hotel_amenity_schema.js';
export {
  hotelCancellationWindows,
  hotelFreeCancellationUntil,
} from './hotel/shared/cancellation_policy.js';
export type { HotelCancellationWindow } from './hotel/shared/cancellation_policy.js';
// A tarifa REPRESENTATIVA do hotel (negociada, senão a mais barata com
// política) — o drawer de detalhes e o foco do agente (agents) falam da MESMA
// tarifa por esta função só.
export { representativeRate } from './hotel/shared/representative_rate.js';
// Puros, usados também pela UI do genui-kit: o catálogo de copy (os builders
// traduzem com ele), as noites de uma estadia e o nome de lugar de um aeroporto.
export * from './shared/i18n_core.js';
export * from './hotel/shared/nights.js';
export { airportPlaceName } from './flight/shared/airport_place_name.js';
export * from './hotel/hotel_planner/hotel_planner_schema.js';
export * from './hotel/hotel_planner/hotel_planner_directives.js';
export * from './hotel/hotel_planner/directive_cuts.js';
export * from './hotel/hotel_planner/hotel_refine_offer.js';
export * from './hotel/hotel_planner/hotel_place_view.js';
export * from './hotel/hotel_planner/hotel_favorites_port.js';
export * from './hotel/hotel_planner/hotel_offer_projection.js';
export * from './hotel/hotel_planner/modal_session_hotel_projection.js';
export {
  HotelDetailsResultSchema,
  HotelOfferDetailResultSchema,
  HOTEL_DETAILS_PHOTOS_MAX,
  HOTEL_DETAILS_AMENITIES_MAX,
  HOTEL_DETAILS_REVIEWS_MAX,
} from './hotel/hotel_planner/hotel_details_schema.js';
export type {
  HotelDetailsResult,
  HotelDetailsReview,
  HotelOfferDetailResult,
} from './hotel/hotel_planner/hotel_details_schema.js';
export * from './hotel/hotel_rooms/hotel_rooms_schema.js';
export * from './places/consult_places/consult_places_schema.js';
export * from './places/consult_places/place_details.js';
export * from './hotel/hotel_rooms/hotel_room_taxonomy.js';
// O rótulo do cartão a partir dos TOKENS da identidade — o agente projeta a
// mesma identidade na cena de quartos (`sceneRoomFromGroup` no agents) com
// esta MESMA função, para o retrato nunca divergir do card.
export { roomIdentityLabel } from './hotel/hotel_rooms/hotel_room_identity_label.js';
// Puro (só tipos importados): a DIÁRIA que o card headline (`DecisionRail`,
// via `hotel_rooms_view.ts`) é a MESMA conta que a cena de quartos do agente
// projeta — sem a função compartilhada os dois arredondamentos podiam
// divergir por centavos (D1).
// A ordem do card por preço (`rankGroups`/`rankRates`, #6914) e a tarifa que a
// linha de cada quarto mostra (`bestRate`, a mesma régua de
// `hotel_rooms_view.ts`): a cena de quartos do agente a projeta para o retrato
// falar da tarifa que o cliente vê (#3223). E o porão (`isOtherRoomsGroup`,
// #5922): a régua única que separa "Outras opções" no card e na cena do agente.
export * from './hotel/hotel_rooms/hotel_rooms_rank.js';
// Os chips de tarifa do cartão de quartos: a mesma régua no destaque do agente
// e na abertura a partir de uma lista recortada (#5820), no agente e na gaveta.
export * from './hotel/hotel_rooms/hotel_rooms_rate_filters.js';
// O tempo estimado até a âncora pela distância (a pé até 1,5 km, de carro
// acima) — a MESMA régua do card, lida pela cena do agente de hotel (#3226).
export { hotelTravelEstimate } from './hotel/shared/travel_estimate.js';
// #3729: a busca cujo lugar é um hotel mede o hotel pela MESMA régua do card.
export { distanceKm, hotelDistanceKm } from './hotel/shared/haversine.js';
export {
  distinctiveTokens,
  offerNamedBy,
  pinPlaceHotel,
  titleTokens,
} from './hotel/shared/named_hotel.js';
// #4090: o BFF traduz só o regime que este catálogo não conhece.
export { localizedBoardLabel } from './hotel/shared/board_label.js';
export * from './hotel/hotel_rate_warning/hotel_rate_warning_schema.js';
export * from './mobility/mobility_offers/mobility_offers_schema.js';
export { buildMobilityOffers } from './mobility/mobility_offers/mobility_offers_builder.js';
export * from './mobility/mobility_planner/mobility_planner_schema.js';
export * from './mobility/mobility_ride_details/mobility_ride_details_schema.js';
export * from './mobility/ride99_link/ride99_link_schema.js';
export * from './bus/bus_offers/bus_offers_schema.js';
export * from './bus/bus_stops/bus_stops_schema.js';
export * from './bus/bus_planner/bus_planner_schema.js';
export * from './bus/bus_seat_map/bus_seat_map_schema.js';
export * from './business/next_services/next_services_schema.js';
export * from './business/checkout_outcome/checkout_outcome_schema.js';
export * from './trips/trips/trips_schema.js';
export * from './trips/flight_seat_link/flight_seat_link_schema.js';
export * from './common/guided_flows/guided_flows_schema.js';
export * from './common/wallet_link/wallet_link_schema.js';
export * from './common/confirm_prompt/confirm_prompt_schema.js';
export * from './common/question_prompt/question_prompt_schema.js';
export * from './events/event_premises/event_premises_schema.js';

// Builders puros (autoria determinística pelas tools do agents; setam o `id`).
export * from './builders.js';

/**
 * Contrato de UM component de UI generativa do canal app (web-chat). O agents
 * monta os blocos via builders e os envia opacos numa LISTA; app-integration
 * valida contra `ChatUiMessageSchema` antes de anexar ao frame SSE. Evolução =
 * adicionar `kind`.
 */
const ChatUiUnionSchema = z.discriminatedUnion('kind', [
  FlightOffersBlockSchema,
  FlightMultiOffersBlockSchema,
  FlightCheaperDatesBlockSchema,
  FlightPlannerBlockSchema,
  RentalOffersBlockSchema,
  RentalPlannerBlockSchema,
  RentalPriceChangeBlockSchema,
  RentalNextServicesBlockSchema,
  HotelPlannerBlockSchema,
  HotelRoomsBlockSchema,
  ConsultPlacesBlockSchema,
  HotelRateWarningBlockSchema,
  HotelNextServicesBlockSchema,
  MobilityOffersBlockSchema,
  MobilityNextServicesBlockSchema,
  MobilityPlannerBlockSchema,
  MobilityRideDetailsBlockSchema,
  Ride99LinkBlockSchema,
  FlightSearchConfirmationBlockSchema,
  FlightReservationBlockSchema,
  FlightNextServicesBlockSchema,
  ProductCheckoutBlockSchema,
  CheckoutOutcomeBlockSchema,
  ApprovalRequestBlockSchema,
  ApprovalResultBlockSchema,
  ApprovalStackBlockSchema,
  ApprovalBulkResultBlockSchema,
  VoucherCancellationBlockSchema,
  VoucherCancellationBulkBlockSchema,
  BusOffersBlockSchema,
  BusStopsBlockSchema,
  BusPlannerBlockSchema,
  BusNextServicesBlockSchema,
  BusSeatMapBlockSchema,
  TripsBlockSchema,
  FlightSeatLinkBlockSchema,
  GuidedFlowsBlockSchema,
  WalletLinkBlockSchema,
  ConfirmPromptBlockSchema,
  QuestionPromptBlockSchema,
  EventPremisesBlockSchema,
]);

export const ChatUiSchema = ChatUiUnionSchema;
export type ChatUi = z.infer<typeof ChatUiSchema>;

/**
 * A composição de um turno: a LISTA de components que o agente emite numa
 * mensagem (modelo A2UI). O shell roteia cada um pelo seu `slot`. Os `id`s são
 * únicos na lista — dedup na borda de render. `domain` carrega isto OPACO
 * (`AppChatMessage.ui: unknown`); o genui-kit é o dono do shape.
 */
export const CHAT_UI_MESSAGE_MAX_BLOCKS = 8;
export const ChatUiMessageSchema = z
  .array(ChatUiSchema)
  .min(1)
  .max(CHAT_UI_MESSAGE_MAX_BLOCKS)
  .refine(
    (blocks) => new Set(blocks.map((b) => b.id)).size === blocks.length,
    'ChatUiMessage: ids duplicados na lista',
  );
export type ChatUiMessage = z.infer<typeof ChatUiMessageSchema>;

/**
 * Âncora em runtime da lista de `kind`s — fonte única para descoberta (CATALOG)
 * e drift-guard. A checagem de exaustividade abaixo falha em compile-time se um
 * `kind` for adicionado ao union sem entrar aqui.
 */
export const CHAT_UI_KINDS = [
  'flight_offers',
  'flight_multi_offers',
  'flight_cheaper_dates',
  'flight_planner',
  'rental_offers',
  'rental_planner',
  'rental_price_change',
  'rental_next_services',
  'hotel_planner',
  'hotel_rooms',
  'consult_places',
  'hotel_rate_warning',
  'hotel_next_services',
  'mobility_offers',
  'mobility_next_services',
  'mobility_planner',
  'mobility_ride_details',
  'ride99_link',
  'flight_search_confirmation',
  'flight_reservation',
  'flight_next_services',
  'product_checkout',
  'checkout_outcome',
  'approval_request',
  'approval_result',
  'approval_stack',
  'approval_bulk_result',
  'voucher_cancellation',
  'voucher_cancellation_bulk',
  'bus_offers',
  'bus_stops',
  'bus_planner',
  'bus_next_services',
  'bus_seat_map',
  'trips',
  'flight_seat_link',
  'guided_flows',
  'wallet_link',
  'confirm_prompt',
  'question_prompt',
  'event_premises',
] as const satisfies readonly ChatUi['kind'][];
export type ChatUiKind = (typeof CHAT_UI_KINDS)[number];

type _MissingKinds = Exclude<ChatUi['kind'], ChatUiKind>;
// `T extends never` fails to compile while some kind is missing. Type-only (`declare`): there is
// no value to keep alive, so nothing has to reference it.
type _AssertNoneMissing<T extends never> = T;
declare const _assertAllKindsListed: _AssertNoneMissing<_MissingKinds>;

/**
 * Contexto de uso de um `kind` — a superfície em que o card faz sentido. Espelha
 * (em inglês) os domínios do agents que têm card dedicado. `common` é transversal
 * (entra em todo catálogo por-contexto): abriga os components cross-domain, que
 * não pertencem a um domínio específico.
 */
export const CHAT_UI_CONTEXTS = [
  'common',
  'flight',
  'rental',
  'hotel',
  'mobility',
  'bus',
  'voucher',
  'trips',
  'approvals',
  'events',
] as const;
export type ChatUiContext = (typeof CHAT_UI_CONTEXTS)[number];

export interface CatalogEntry {
  /** Carrega ação que resolve uma vez (confirmação/aprovação)? */
  interactive: boolean;
  /** Caminho (dot-path) de onde ler o interactionId quando interativo. */
  interactionPath?: string;
  /** Contextos onde o kind é válido (≥1). `common` aparece em todo contexto. */
  contexts: readonly [ChatUiContext, ...ChatUiContext[]];
}

export const CATALOG = {
  // Expansão de tarifas inline: quando o bloco traz `challengeId` (produtor v2)
  // o pick sai por `interaction` (selections `select_offer_fares`). Payload
  // legado (sem challengeId) → `interactionIdOf` = undefined → display-only.
  flight_offers: { interactive: true, interactionPath: 'challengeId', contexts: ['flight'] },
  // Um card por ROTEIRO: a escolha de cada trecho é estado do componente e o
  // único `interaction` sai do CTA final, carregando os N picks.
  flight_multi_offers: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['flight'],
  },
  // Datas mais baratas (gate pré-ofertas). Toque numa data volta por `interaction`
  // (challengeId no topo do bloco) — o LLM relê o texto e chama search_flights.
  flight_cheaper_dates: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['flight'],
  },
  // Planner de voo (filtro pré-ofertas SEMPRE exibido, agents-v2). Echo por
  // `interaction` com selections discriminado por `action` (search|requery) —
  // o map-guard do flight roteia sem LLM.
  flight_planner: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['flight'],
  },
  rental_offers: { interactive: false, contexts: ['rental'] },
  // A busca de carro na conversa (#5641) — o Buscar devolve os lugares (ref) e a
  // janela por `interaction` (challengeId no topo do bloco); resolvida, congela.
  rental_planner: { interactive: true, interactionPath: 'challengeId', contexts: ['rental'] },
  // Mudança de preço — challengeId opcional (interaction quando presente, senão
  // reply); interactionIdOf degrada p/ undefined sem challenge.
  rental_price_change: {
    interactive: true,
    interactionPath: 'priceChange.challengeId',
    contexts: ['rental'],
  },
  // Paridade com flight_next_services (#5514): sugestões pós-carro (linhas
  // dispatcham `reply`, sem challenge) + o carrinho do recibo provisório do
  // painel da viagem no host.
  rental_next_services: { interactive: false, contexts: ['rental'] },
  // Planner e resultados no mesmo lugar. Critérios válidos disparam
  // HotelPlannerSelection em `challengeId` (guarded → submit_planner_search).
  hotel_planner: { interactive: true, interactionPath: 'challengeId', contexts: ['hotel'] },
  // Escolha de quarto pós-seleção de hotel — dispara hotel-room-select
  // (selection estruturada), sem challenge.
  hotel_rooms: { interactive: false, contexts: ['hotel'] },
  // Recomendações da consulta pré-hotel: display-only; dados Google são lidos ao vivo.
  consult_places: { interactive: false, contexts: ['hotel'] },
  hotel_rate_warning: { interactive: false, contexts: ['hotel'] },
  // Paridade com flight_next_services (#2116/#2349): sugestões pós-reserva
  // (linhas dispatcham `reply`, sem challenge) + dados do recibo provisório do
  // painel da viagem no host.
  hotel_next_services: { interactive: false, contexts: ['hotel'] },
  mobility_offers: { interactive: false, contexts: ['mobility'] },
  // Sinal «entrou na viagem» da mobilidade (#5279): o host adota o recibo
  // provisório e o painel da viagem. Com `services` (#5514) desenha as
  // sugestões pós-corrida; sem eles, data-only.
  mobility_next_services: { interactive: false, contexts: ['mobility'] },
  // Escolha de origem/destino do agendamento de carro quando o agente não teve
  // certeza: o Buscar responde o challenge com os `ref`s dos lugares.
  mobility_planner: { interactive: true, interactionPath: 'challengeId', contexts: ['mobility'] },
  // Telefone do passageiro e observações depois da escolha (#6699): o
  // Continuar responde o challenge com a `MobilityRideDetailsSubmit`.
  mobility_ride_details: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['mobility'],
  },
  // Atalho para a folha de pedido da 99 (#6889), emitido pelo mobility na corrida
  // imediata: display-only — o botão abre a folha (`open-ride99`, client-side), sem challenge.
  ride99_link: { interactive: false, contexts: ['mobility'] },
  flight_search_confirmation: { interactive: false, contexts: ['flight'] },
  flight_reservation: { interactive: false, contexts: ['flight'] },
  // Pós-emissão do aéreo (#2116): sugestões de próximo serviço + dados do
  // recibo provisório do painel da viagem. Display-only (as linhas dispatcham
  // `reply`, sem challenge server-side).
  flight_next_services: { interactive: false, contexts: ['flight'] },
  // Unified checkout — rental + hotel + mobility (see design spec). "Continuar"
  // opens the drawer; submit resolves the `challengeId` structurally (anti-LLM).
  product_checkout: {
    interactive: true,
    interactionPath: 'checkout.challengeId',
    contexts: ['rental', 'hotel', 'mobility'],
  },
  // Desfecho mínimo do checkout (#5360): data-only, sai no turno da reserva
  // quando o `*_next_services` não sai, e na recusa (`failed`, #5514). O host o
  // lê pelo `challengeId`.
  checkout_outcome: {
    interactive: false,
    contexts: ['flight', 'hotel', 'bus', 'rental', 'mobility'],
  },
  // Voucher approval round-trip. The two buttons (Aprovar/Reprovar) echo ONE
  // challengeId; the chosen decision rides alongside it in the confirmation
  // payload (not a second challenge).
  approval_request: {
    interactive: true,
    interactionPath: 'request.challengeId',
    contexts: ['approvals'],
  },
  approval_result: { interactive: false, contexts: ['approvals'] },
  // Card empilhado de aprovações (traveler → trip → voucher). Interativo — o
  // interactionId do topo é o challenge `all`; o congelamento por-voucher/por-grupo
  // é tratado internamente pelo card via re-emit do agente.
  approval_stack: {
    interactive: true,
    interactionPath: 'allChallengeId',
    contexts: ['approvals'],
  },
  // Resultado parcial de uma aprovação em lote (approved/failed) — display-only.
  approval_bulk_result: { interactive: false, contexts: ['approvals'] },
  // Partidas de ônibus. Com `challengeId` (o card vivo) a viagem e as poltronas
  // de cada trecho voltam por `interaction` — resolvido, o card congela. Bloco
  // legado (sem challengeId) → `interactionIdOf` = undefined → o pick é `reply`.
  bus_offers: { interactive: true, interactionPath: 'challengeId', contexts: ['bus'] },
  // Desambiguação de rodoviária — display-only. O toque numa candidata volta por
  // `reply` com o nome exato; os códigos ficam server-side (pendingStopCandidates).
  bus_stops: { interactive: false, contexts: ['bus'] },
  // A busca de ônibus na conversa — o Buscar devolve as rodoviárias e os dias por
  // `interaction` (challengeId no topo do bloco); resolvida, o card congela.
  bus_planner: { interactive: true, interactionPath: 'challengeId', contexts: ['bus'] },
  // Mapa de poltronas — a escolha volta por `interaction` (challengeId no topo do
  // bloco); o seatCode viaja ESTRUTURADO (anti-LLM), igual ao fare selector.
  bus_seat_map: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['bus'],
  },
  // Próximos serviços pós-passagem — as linhas mandam o pedido por `reply`.
  bus_next_services: { interactive: false, contexts: ['bus'] },
  // Confirmação de cancelamento de voucher — o "Confirmar" resolve o challenge
  // estruturado (anti-LLM), guardrail de ação destrutiva.
  voucher_cancellation: {
    interactive: true,
    interactionPath: 'confirmation.challengeId',
    contexts: ['voucher'],
  },
  // Cancelamento em LOTE — checkbox multi-seleção + submit único. O interactionId
  // do topo é o challenge de sessão que cobre o submit inteiro; os códigos
  // marcados viajam em `interaction.data` → `confirmation.selections` (opaco).
  voucher_cancellation_bulk: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['voucher'],
  },
  trips: { interactive: false, contexts: ['trips'] },
  // Atalho para a escolha de assento do voo emitido (agente de viagem): display-only — o
  // botão navega (`open-trip`, client-side), sem challenge.
  flight_seat_link: { interactive: false, contexts: ['trips'] },
  // Atalhos ilustrados de reserva manual — transversais (`common`) e sem
  // challenge: o toque manda uma mensagem com o domínio escolhido
  // (`domainHint`), que o supervisor usa para rotear sem classificar a prosa.
  guided_flows: { interactive: false, contexts: ['common'] },
  // Atalho para a Carteira do app (#6278), emitido pela recepção: display-only — o botão
  // navega (`open-wallet`, client-side), sem challenge.
  wallet_link: { interactive: false, contexts: ['common'] },
  // Prompt de confirmação genérico (slot chat) — duas respostas estruturadas via
  // challengeId; o domínio emissor manda a copy localizada no payload.
  confirm_prompt: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['common'],
  },
  // Pergunta estruturada genérica (M6): N opções clicáveis; o pick sai por
  // `interaction` (selections `select_option`) e o map-guard do domínio emissor
  // roteia sem LLM.
  question_prompt: {
    interactive: true,
    interactionPath: 'challengeId',
    contexts: ['common'],
  },
  // Linha da ficha de premissas do evento (grupos e eventos): só exibe; o
  // web-chat abre a folha de premissas a partir dela.
  event_premises: { interactive: false, contexts: ['events'] },
} as const satisfies Record<ChatUiKind, CatalogEntry>;

/**
 * O `kind` pertence ao catálogo do `context`? Um contexto específico inclui os
 * seus kinds MAIS os `common` (transversais); o contexto `common` inclui só os
 * próprios.
 */
export function isKindInContext(kind: ChatUiKind, context: ChatUiContext): boolean {
  const contexts: readonly ChatUiContext[] = CATALOG[kind].contexts;
  if (context === 'common') return contexts.includes('common');
  return contexts.includes(context) || contexts.includes('common');
}

/** Os `kind`s do catálogo de um contexto (ordem estável de `CHAT_UI_KINDS`). */
export function kindsFor(context: ChatUiContext): ChatUiKind[] {
  return CHAT_UI_KINDS.filter((kind) => isKindInContext(kind, context));
}

/**
 * Catálogo fatiado por contexto — derivado de `CATALOG` (fonte única). Cada
 * sub-catálogo carrega as MESMAS entradas (interactive/interactionPath/contexts)
 * do mestre, só filtradas pelo contexto. `common` entra em todos.
 */
export const CATALOGS: Record<
  ChatUiContext,
  Partial<Record<ChatUiKind, CatalogEntry>>
> = Object.fromEntries(
  CHAT_UI_CONTEXTS.map((context) => [
    context,
    Object.fromEntries(
      (Object.entries(CATALOG) as Array<[ChatUiKind, CatalogEntry]>).filter(([kind]) =>
        isKindInContext(kind, context),
      ),
    ),
  ]),
) as Record<ChatUiContext, Partial<Record<ChatUiKind, CatalogEntry>>>;

/** Lê um valor por dot-path (`a.b.c`) de um objeto, ou `undefined` se ausente. */
function readPath(obj: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc !== null && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * interactionId de um bloco interativo (= challengeId nos destrutivos), ou
 * `undefined` para blocos display-only. É o identificador unificado da
 * resolução por-card (Fase 2): idempotência + congelar o card certo. Derivado
 * do `interactionPath` do `CATALOG` — fonte única, sem switch paralelo.
 */
export function interactionIdOf(ui: ChatUi): string | undefined {
  const entry = CATALOG[ui.kind];
  if (!entry.interactive) return undefined;
  const value = readPath(ui, entry.interactionPath);
  return typeof value === 'string' ? value : undefined;
}
